import React, { useState } from 'react';
import clsx from 'clsx';
import { useTheme } from '@material-ui/core/styles';
import FormHelperText from '@material-ui/core/FormHelperText';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Input from '@material-ui/core/Input';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Checkbox from '@material-ui/core/Checkbox';
import ListItemText from '@material-ui/core/ListItemText';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Button from '@material-ui/core/Button';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import TextField from '@material-ui/core/TextField';
import Chip from '@material-ui/core/Chip';
import { Typography } from '@material-ui/core';
import { getTextEngine } from '@mummssoftware/utils/utils';
import { ActionIcon } from '../ActionIcon';
import { ReactDatePicker } from '../DatePicker';
import { isonerequired } from './constants';
import { useStyles } from './style';
import { Text } from './WrapperDiv';
import { checkRequired } from './utils';
const helperText = `Please check at least one checkbox`;
export const LabelField = /*#__PURE__*/React.memo(({
  subqchoice,
  onClick,
  isExpanded,
  submittedState
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(isExpanded || false);
  const classes = useStyles();
  const {
    required
  } = subqchoice;
  let isRequired;

  if (required) {
    if (submittedState && submittedState[subqchoice.questionpath]) {
      // if testPassed means all the children have been answered
      const data = required && subqchoice.subqchoices;
      const testPassed = checkRequired(data, submittedState);
      isRequired = !testPassed;
    } else {
      isRequired = required;
    }
  }

  const rootClass = isRequired ? classes.requiredFieldLabel : classes.fieldLabel;
  const iconColor = isRequired ? theme.palette.error.main : undefined;
  const showHelper = subqchoice.rule === isonerequired && expanded;

  const handleClick = () => {
    setExpanded(!expanded);
    onClick(!expanded);
  };

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, null, /*#__PURE__*/React.createElement(ActionIcon, {
    style: {
      margin: 0
    },
    htmlColor: iconColor,
    Icon: expanded ? ExpandMoreIcon : ChevronRightIcon,
    onClick: handleClick,
    value: expanded
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "body1",
    classes: {
      root: rootClass
    }
  }, subqchoice.questionlabel)), showHelper && /*#__PURE__*/React.createElement(FormHelperText, {
    classes: {
      root: classes.helperText
    }
  }, helperText));
});
export const StatementField = /*#__PURE__*/React.memo(({
  subqchoice
}) => {
  // css title with bigger font
  const isTitle = subqchoice.cssclass && subqchoice.cssclass === 'title';
  const classes = useStyles();
  const rootClass = isTitle ? classes.statementTitle : classes.fieldLabel;
  return /*#__PURE__*/React.createElement(Text, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "body1",
    classes: {
      root: rootClass
    }
  }, subqchoice.questionlabel));
});
export const ReadOnlyStatementField = /*#__PURE__*/React.memo(({
  text,
  title
}) => {
  // css title with bigger font
  const isTitle = title && title === 'title';
  const classes = useStyles();
  const rootClass = isTitle ? classes.statementReadTitle : classes.statementField;
  return /*#__PURE__*/React.createElement(Text, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "body1",
    classes: {
      root: rootClass
    }
  }, text));
}); // ReadOnly Form

export const ReadLabelField = /*#__PURE__*/React.memo(({
  subqchoice,
  isExpanded
}) => {
  const classes = useStyles();
  return /*#__PURE__*/React.createElement(Text, null, /*#__PURE__*/React.createElement(ActionIcon, {
    style: {
      margin: 0
    },
    Icon: isExpanded ? ExpandMoreIcon : ChevronRightIcon,
    id: "form-chevron-icon"
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "body1",
    classes: {
      root: classes.readLabel
    }
  }, subqchoice.questionlabel));
});
export const InputField = /*#__PURE__*/React.memo(({
  handleChange,
  label,
  disabled,
  value
}) => {
  const classes = useStyles();
  /** format the string */

  const formattedInput = getTextEngine(value);
  const [textValue, setValue] = useState(formattedInput);

  const handleTextChange = event => {
    setValue(event.target.value);
  };

  const handleOnSave = e => {
    handleChange(textValue); // handleChange(e.target.value);
  };

  return /*#__PURE__*/React.createElement(TextField, {
    id: "text-field-cpp",
    label: label || 'Comment',
    variant: "filled",
    multiline: true,
    value: textValue,
    disabled: disabled,
    onChange: handleTextChange,
    className: clsx(classes.largeTextField),
    onBlur: handleOnSave,
    InputLabelProps: {
      shrink: true
    }
  });
});
export const DateField = /*#__PURE__*/React.memo(({
  label,
  value,
  handleChange
}) => /*#__PURE__*/React.createElement(ReactDatePicker, {
  value: value || new Date(),
  onChange: handleChange,
  placeholderText: label
}));
export const SelectField = /*#__PURE__*/React.memo(({
  checked,
  subqchoice,
  handleChange
}) => {
  const classes = useStyles();
  const hasValue = subqchoice.subqchoices && subqchoice.subqchoices.length && subqchoice.subqchoices[0] !== null;
  let selectValue = ''; // keys = [] or ['27571->25005->25609']

  const keys = Object.keys(checked);

  if (keys.length) {
    // eslint-disable-next-line prefer-destructuring
    selectValue = keys[0];
  }

  return (// if autounroll this is the parent node
    subqchoice.autounroll && /*#__PURE__*/React.createElement(FormControl, {
      className: classes.formControl
    }, /*#__PURE__*/React.createElement(InputLabel, {
      shrink: true
    }, `${subqchoice.questionlabel}`), /*#__PURE__*/React.createElement(Select, {
      value: hasValue && selectValue,
      onChange: handleChange,
      displayEmpty: true,
      name: subqchoice.questionlabel
    }, hasValue && subqchoice.subqchoices.map(choice => /*#__PURE__*/React.createElement(MenuItem, {
      key: choice.questionlabel,
      value: choice.questionpath
    }, `${choice.questionlabel}`))))
  );
});

var _ref = /*#__PURE__*/React.createElement(Input, null);

export const MultiSelectField = /*#__PURE__*/React.memo(({
  handleChange,
  subqchoice,
  checked
}) => {
  const classes = useStyles();
  const {
    subqchoices
  } = subqchoice;
  const hasValue = subqchoices && subqchoices.length && subqchoices[0] !== null;
  let selectValue = []; // checked is {27563->25040->25357->24979->24965: true}
  // keys = [] or ['27571->25005->25609']

  const keys = Object.keys(checked);

  if (keys.length) {
    // eslint-disable-next-line prefer-destructuring
    selectValue = keys;
  }

  const names = hasValue ? subqchoices.reduce((seed, value) => {
    seed[value.questionpath] = value.questionlabel;
    return seed;
  }, {}) : [];
  const MenuProps = {
    variant: 'menu',
    getContentAnchorEl: null // to get rid of the jumping.
    // https://stackoverflow.com/questions/59785482/multiselect-box-popover-keeps-jumping-when-scroll-or-select-items

  };
  return (// if autounroll this is the parent node
    subqchoice.autounroll && /*#__PURE__*/React.createElement(FormControl, {
      className: classes.formControl
    }, /*#__PURE__*/React.createElement(InputLabel, {
      shrink: true
    }, subqchoice.questionlabel), /*#__PURE__*/React.createElement(Select, {
      value: selectValue,
      onChange: handleChange,
      displayEmpty: true,
      multiple: true,
      input: _ref,
      renderValue: selected => {
        const values = selected.map(el => names[el]);
        return values.join(', ');
      },
      name: subqchoice.questionlabel,
      MenuProps: MenuProps
    }, hasValue && subqchoices.map(choice => /*#__PURE__*/React.createElement(MenuItem, {
      key: choice.questionlabel,
      value: choice.questionpath
    }, /*#__PURE__*/React.createElement(Checkbox, {
      checked: !!checked[choice.questionpath],
      color: "primary",
      classes: {
        root: classes.defaultColor
      },
      name: choice.questionlabel
    }), /*#__PURE__*/React.createElement(ListItemText, {
      primary: choice.questionlabel
    })))))
  );
});

const getFieldClasses = (classes, required, readonly) => {
  if (required) {
    return {
      root: classes.required
    };
  } else if (readonly) {
    return {
      root: classes.readOnly
    };
  }

  return {
    root: classes.defaultColor
  };
};

const getLabelClasses = (classes, required) => {
  if (required) {
    return {
      label: classes.requiredLabel
    };
  }

  return {
    label: classes.label
  };
};

export const CheckBoxField = /*#__PURE__*/React.memo(({
  checked,
  subqchoice,
  handleChange,
  readonly,
  checkboxChecked
}) => {
  const classes = useStyles();
  const {
    required
  } = subqchoice;
  const isNoRuleRequired = required && !checked;
  const isAtLeastRequired = required && !checkboxChecked;
  const isRequired = subqchoice.rule === isonerequired ? isAtLeastRequired : isNoRuleRequired;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Checkbox, {
      checked: checked,
      onChange: handleChange,
      name: subqchoice.message,
      classes: getFieldClasses(classes, isRequired, readonly),
      color: "primary"
    }),
    label: `${subqchoice.questionlabel}`,
    key: subqchoice.id,
    classes: getLabelClasses(classes, isRequired)
  }));
});
export const RadioField = /*#__PURE__*/React.memo(({
  checked,
  radioChecked,
  subqchoice,
  handleChange,
  readonly
}) => {
  const classes = useStyles();
  const {
    required
  } = subqchoice; // isRequired is more complex, depends if one checkbox was selected

  const isRequired = required && !radioChecked;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(RadioGroup, {
    "aria-label": "gender",
    name: "radio-group",
    value: checked
  }, /*#__PURE__*/React.createElement(FormControlLabel, {
    key: subqchoice.questionpath,
    name: "radio-cpp" // eslint-disable-next-line react/jsx-boolean-value
    ,
    value: true,
    control: /*#__PURE__*/React.createElement(Radio, {
      id: subqchoice.questionpath,
      size: "small",
      color: "primary",
      name: "radio-cpp",
      classes: getFieldClasses(classes, isRequired, readonly),
      onClick: handleChange
    }),
    label: subqchoice.questionlabel,
    classes: getLabelClasses(classes, isRequired)
  })));
});
export const DCButton = /*#__PURE__*/React.memo(({
  handleChange,
  radio,
  disabled
}) => {
  const classes = useStyles();
  const rootClass = radio ? classes.radioButtonRoot : classes.buttonRoot;
  return /*#__PURE__*/React.createElement(Button, {
    type: "submit",
    variant: "outlined",
    size: "small",
    onClick: handleChange,
    disabled: disabled,
    color: "primary",
    classes: {
      label: classes.buttonLabel,
      root: rootClass,
      outlined: classes.buttonOutlined
    }
  }, "DC");
});
export const RequiredChip = /*#__PURE__*/React.memo(({
  text
}) => {
  const classes = useStyles();
  return /*#__PURE__*/React.createElement(Chip, {
    label: text,
    classes: {
      root: classes.chipRoot
    }
  });
});